/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link, useParams } from 'react-router-dom';
import { useLinkStyles } from 'components/common/useLinkStyles';
import WelcomeSection from '../../components/welcomeSection/WelcomeSection';
import useEPCTIframe from './useEPCTIframe';
import EPCTErrorPage from './EPCTErrorPage';
import { RoutePath } from '../../shared/enums/RoutePath';

const useStyles = makeStyles({
  iframe: {
    width: '100%',
    minHeight: 'calc(100vh - 10.1875rem)',
  },
});

export default function EPCTIframe() {
  const { t } = useTranslation();
  const classes = useStyles();
  const linkClass = useLinkStyles();

  const { procedure, initialSubmissionProcedure } = useParams();
  document.title = procedure === 'PCTSFD' ? 'PCT SFD online filing' : 'PCT RO online filing';

  const { epctURL, oid, loadingError } = useEPCTIframe({ procedure, initialSubmissionProcedure });

  const iframe = useRef();

  const iframeElem = iframe.current;

  // In order to be able to navigate within the iframe via the anchor tags
  // We need to change the anchor tags to point to a parent link instead of calling a javascript function
  const modifyAnchorTags = (doc) => {
    // Retrieve all the anchor tags from the iframe document
    const anchors = doc.getElementsByTagName('a');
    for (const anchor of anchors) {
      const { href } = anchor;

      // Change just the anchor tags which are pointing to a function
      if (href?.includes('javascript')) {
        const sectionId = href.substring(href.indexOf('\'') + 1, href.lastIndexOf('\''));
        anchor.href = `/ePCTFiling/pages/embedded/Draft.xhtml?oid=${oid}#${sectionId}`;
      }
    }
  };

  // We need this event listener for scenario 2, because some of the anchor tags are rendered just when the user is interacting with the iframe
  const manageEventsListeners = (register = true) => {
    const doc = iframeElem.contentDocument ? iframeElem.contentDocument : iframeElem.contentWindow.document;
    if (!doc) return;
    if (register) doc.addEventListener('click', () => modifyAnchorTags(doc));
    if (!register) doc.removeEventListener('click', () => modifyAnchorTags(doc));
  };

  const unregisterListeners = () => {
    iframeElem.removeEventListener('load', manageEventsListeners);
    manageEventsListeners(false);
  };

  useEffect(() => {
    if (iframeElem && oid) {
      iframeElem.addEventListener('load', manageEventsListeners, { passive: false });

      return () => unregisterListeners();
    }

    return () => {
      // no cleanup
    };
  }, [iframeElem, oid]);

  const breadcrumbTo = RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, {
    procedure,
  });

  return (
    <>
      <Breadcrumbs aria-label="breadcrumb" className="breadcrumbs">
        <Link className={linkClass.link} to={breadcrumbTo}>
          {t('submission.epct.title')}
        </Link>
        <Typography color="textPrimary">{t('submission.epct.title.patent')}</Typography>
      </Breadcrumbs>

      <WelcomeSection title={t('draft.epct.title')} description={t('draft.epct.description')} />

      {!loadingError && <iframe ref={iframe} className={classes.iframe} title={t('draft.epct.title')} src={epctURL} />}

      {loadingError && <EPCTErrorPage />}
    </>
  );
}
